import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const useDynamicQRStats = (generatedQRCodes, dynamicQRHook) => {
  const [dynamicQRData, setDynamicQRData] = useState({});
  const [isFetchingDynamic, setIsFetchingDynamic] = useState({});
  const [editingUrl, setEditingUrl] = useState({});
  const [newDestinationUrl, setNewDestinationUrl] = useState({});
  const [updateMessage, setUpdateMessage] = useState({});
  const [editingContent, setEditingContent] = useState({});
  const [editedContent, setEditedContent] = useState({});

  // Detect if QR is dynamic
  const isDynamic = (qr) => qr.isDynamic || qr.qrType === 'dynamic';

  // Format timestamp to human-readable "time ago"
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  // Fetch dynamic QR data from Firestore
  const fetchDynamicQRData = async (qrId) => {
    if (!qrId) return;
    
    setIsFetchingDynamic(prev => ({ ...prev, [qrId]: true }));
    
    try {
      const docRef = doc(db, 'dynamic_qr_codes', qrId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        const totalDeviceScans = 
          (data.deviceStats?.mobile || 0) + 
          (data.deviceStats?.desktop || 0) + 
          (data.deviceStats?.tablet || 0);
        
        const deviceStats = totalDeviceScans > 0 ? {
          mobile: Math.round(((data.deviceStats?.mobile || 0) / totalDeviceScans) * 100),
          desktop: Math.round(((data.deviceStats?.desktop || 0) / totalDeviceScans) * 100),
          tablet: Math.round(((data.deviceStats?.tablet || 0) / totalDeviceScans) * 100)
        } : null;

        const geoStats = data.geoStats || {};
        const topLocation = Object.keys(geoStats).length > 0
          ? Object.entries(geoStats).sort((a, b) => b[1] - a[1])[0][0]
          : null;

        setDynamicQRData(prev => ({
          ...prev,
          [qrId]: {
            scanCount: data.scanCount || 0,
            uniqueScans: data.uniqueScans || 0,
            scansToday: data.scansToday || 0,
            scansThisWeek: data.scansThisWeek || 0,
            isActive: data.isActive !== undefined ? data.isActive : true,
            lastScannedAt: data.lastScannedAt,
            qrContent: data.qrContent,
            shortId: data.shortId,
            scans: data.scans || [],
            expiresAt: data.expiresAt,
            createdAt: data.createdAt?.toDate(),
            originalContent: data.qrContent,
            deviceStats,
            topLocation,
            geoStats
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching dynamic QR data:', error);
    } finally {
      setIsFetchingDynamic(prev => ({ ...prev, [qrId]: false }));
    }
  };

  // Auto-fetch data for all dynamic QR codes
  useEffect(() => {
    generatedQRCodes.forEach(qr => {
      if (isDynamic(qr) && qr.id) {
        if (!dynamicQRData[qr.id]) {
          fetchDynamicQRData(qr.id);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatedQRCodes]);

  // Handle updating destination URL
  const handleUpdateDestination = async (qrId) => {
    const newUrl = newDestinationUrl[qrId];
    
    if (!newUrl || !newUrl.trim()) {
      setUpdateMessage(prev => ({ ...prev, [qrId]: { type: 'error', text: '⚠️ Please enter a valid URL' } }));
      setTimeout(() => setUpdateMessage(prev => ({ ...prev, [qrId]: null })), 3000);
      return;
    }

    if (!newUrl.startsWith('http://') && !newUrl.startsWith('https://')) {
      setUpdateMessage(prev => ({ ...prev, [qrId]: { type: 'error', text: '⚠️ URL must start with http:// or https://' } }));
      setTimeout(() => setUpdateMessage(prev => ({ ...prev, [qrId]: null })), 3000);
      return;
    }

    try {
      setUpdateMessage(prev => ({ ...prev, [qrId]: { type: 'loading', text: 'Updating...' } }));
      await dynamicQRHook.updateDynamicQR(qrId, { qrContent: newUrl });
      await fetchDynamicQRData(qrId);
      setUpdateMessage(prev => ({ ...prev, [qrId]: { type: 'success', text: '✓ Updated!' } }));
      setEditingUrl(prev => ({ ...prev, [qrId]: false }));
      setTimeout(() => setUpdateMessage(prev => ({ ...prev, [qrId]: null })), 3000);
    } catch (error) {
      setUpdateMessage(prev => ({ ...prev, [qrId]: { type: 'error', text: '✗ Failed to update' } }));
      setTimeout(() => setUpdateMessage(prev => ({ ...prev, [qrId]: null })), 3000);
    }
  };

  // Handle toggling QR status (active/paused)
  const handleToggleStatus = async (qrId, currentStatus) => {
    const newStatus = !currentStatus;
    try {
      setDynamicQRData(prev => ({ ...prev, [qrId]: { ...prev[qrId], isActive: newStatus } }));
      await dynamicQRHook.toggleQRStatus(qrId, newStatus);
      setUpdateMessage(prev => ({ ...prev, [qrId]: { type: 'success', text: newStatus ? '✓ QR Activated' : '✓ QR Paused' } }));
      setTimeout(() => setUpdateMessage(prev => ({ ...prev, [qrId]: null })), 3000);
    } catch (error) {
      setDynamicQRData(prev => ({ ...prev, [qrId]: { ...prev[qrId], isActive: currentStatus } }));
      setUpdateMessage(prev => ({ ...prev, [qrId]: { type: 'error', text: '✗ Failed to update status' } }));
      setTimeout(() => setUpdateMessage(prev => ({ ...prev, [qrId]: null })), 3000);
    }
  };

  // Start editing URL
  const startEditingUrl = (qrId, currentUrl) => {
    setEditingUrl(prev => ({ ...prev, [qrId]: true }));
    setNewDestinationUrl(prev => ({ ...prev, [qrId]: currentUrl }));
  };

  // Cancel editing URL
  const cancelEditingUrl = (qrId) => {
    setEditingUrl(prev => ({ ...prev, [qrId]: false }));
    setNewDestinationUrl(prev => ({ ...prev, [qrId]: '' }));
  };

  // Start editing content
  const startEditingContent = (qrId, currentContent) => {
    setEditingContent(prev => ({ ...prev, [qrId]: true }));
    setEditedContent(prev => ({ ...prev, [qrId]: currentContent }));
  };

  // Cancel editing content
  const cancelEditingContent = (qrId, originalContent) => {
    setEditingContent(prev => ({ ...prev, [qrId]: false }));
    setEditedContent(prev => ({ ...prev, [qrId]: originalContent }));
  };

  // Update edited content value
  const updateEditedContent = (qrId, value) => {
    setEditedContent(prev => ({ ...prev, [qrId]: value }));
  };

  // Update new destination URL value
  const updateNewDestinationUrl = (qrId, value) => {
    setNewDestinationUrl(prev => ({ ...prev, [qrId]: value }));
  };

  return {
    // State
    dynamicQRData,
    isFetchingDynamic,
    editingUrl,
    newDestinationUrl,
    updateMessage,
    editingContent,
    editedContent,
    
    // Functions
    isDynamic,
    formatTimeAgo,
    fetchDynamicQRData,
    handleUpdateDestination,
    handleToggleStatus,
    startEditingUrl,
    cancelEditingUrl,
    startEditingContent,
    cancelEditingContent,
    updateEditedContent,
    updateNewDestinationUrl
  };
};

export default useDynamicQRStats;